
import React from 'react';
import DashboardSidebar, { menuItems } from './DashboardSidebar';
import { ThemeToggle } from './ThemeToggle';
import { cn } from '@/lib/utils';
import { Menu, HelpCircle, Rocket, ArrowRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Link } from 'react-router-dom';
import LogoSvg from './LogoSvg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import Footer from './Footer';
import { Separator } from './ui/separator';
import { Button } from './ui/button';

interface DashboardLayoutProps {
  children: React.ReactNode;
  className?: string;
}

const DashboardLayout = ({ children, className }: DashboardLayoutProps) => {
  const isMobile = useIsMobile();

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <DashboardSidebar />
      <main className={cn(
        "flex-1 overflow-y-auto scrollbar-none px-4 sm:px-6 md:px-10 py-6 relative flex flex-col",
        className
      )}>
        {isMobile && (
          <div className="flex items-center justify-between mb-6 px-2">
            <div className="flex items-center">
              <Link to="/">
                <LogoSvg className="h-8" />
              </Link>
            </div>
          </div>
        )}
        <div className="absolute right-4 sm:right-6 md:right-10 top-6 z-10 flex items-center gap-2">
          {!isMobile && (
            <Button 
              variant="outline" 
              className="rounded-full flex items-center gap-2 launch-button" 
              asChild
              external
            >
              <a href="https://app.openpipe.ai/">
                <Rocket className="h-[1.2rem] w-[1.2rem] group-hover:text-white" />
                <span className="group-hover:text-white">Launch App</span>
              </a>
            </Button>
          )}
          {isMobile ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full p-2 bg-secondary/80 hover:bg-secondary/100 transition-colors">
                  <Menu size={24} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 z-50 max-h-[80vh] overflow-y-auto">
                {menuItems.map((item) => (
                  <DropdownMenuItem 
                    key={item.label} 
                    asChild 
                    className="py-3 cursor-pointer focus:bg-accent focus:text-accent-foreground"
                  >
                    {item.isExternal ? (
                      <a 
                        href={item.path} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center w-full"
                      >
                        <item.icon className="mr-3 h-5 w-5" />
                        <span>{item.label}</span>
                      </a>
                    ) : (
                      <Link to={item.path} className="flex items-center w-full">
                        <item.icon className="mr-3 h-5 w-5" />
                        <span>{item.label}</span>
                      </Link>
                    )}
                  </DropdownMenuItem>
                ))}
                
                <DropdownMenuItem 
                  asChild 
                  className="py-3 cursor-pointer focus:bg-accent focus:text-accent-foreground"
                >
                  <a href="https://app.openpipe.ai/" className="flex items-center w-full">
                    <Rocket className="mr-3 h-5 w-5" />
                    <span>Launch App</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem className="py-3 cursor-pointer focus:bg-accent focus:text-accent-foreground">
                  <a href="mailto:hello@openpipe.ai" className="flex items-center w-full">
                    <HelpCircle className="mr-3 h-5 w-5" />
                    <span>Help & Support</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="flex justify-center items-center py-3 focus:bg-accent focus:text-accent-foreground"
                  onSelect={(e) => e.preventDefault()}
                >
                  <ThemeToggle />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <ThemeToggle />
          )}
        </div>
        <div className="pt-10 sm:pt-12 md:pt-14 mx-auto w-[95%] sm:w-[94%] md:max-w-full flex-grow">
          {children}
        </div>
        <Separator className="mt-10" />
        <Footer />
      </main>
    </div>
  );
};

export default DashboardLayout;
